import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Film, Loader2, Send, Sparkles, MousePointerClick, Link2, Info, ExternalLink } from "lucide-react";
import { StoryboardPanel } from "./StoryboardPanel";
import { SlideThumb } from "../../components/social/SlideThumb.js";
import { ROLE_HE } from "../../components/social/deckGoals.js";
import {
  api,
  type SocialPackView,
  type SocialVariantView,
  type SocialCheckIssue,
  type SocialPlatform,
  type SocialFormat, type PackRefreshJobView } from "../../lib/api.js";

/**
 * Social pack review.
 *
 * The screen exists so that nothing reaches a public feed without a person
 * having read it. Generation always produces DRAFTS, and the send button creates
 * a draft in the Postiz calendar rather than publishing — approving there is a
 * second, deliberate act.
 *
 * The carousel preview is an <iframe> onto `/api/social/slide`, the SAME
 * document the PNG exporter screenshots. A preview that renders differently from
 * the export is a bug you only discover once it is already on Instagram, so
 * there is exactly one renderer and both callers use it.
 */

const PLATFORM_HE: Record<SocialPlatform, string> = {
  facebook: "פייסבוק",
  instagram: "אינסטגרם",
  tiktok: "טיקטוק",
  youtube: "יוטיוב",
};

const FORMAT_HE: Record<SocialFormat, string> = {
  feed_link: "פוסט פיד + לינק",
  feed_image: "פוסט תמונה",
  carousel: "קרוסלה",
  reel: "ריל",
  story: "סטורי",
  video: "וידאו",
  photo_post: "פוסט תמונות",
  short: "שורט",
  long_video: "סרטון ארוך",
};

/** Date and time in Israel, for "published at" and "scheduled for". */
const fmtIl = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", {
    timeZone: "Asia/Jerusalem", weekday: "short", day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(iso));

/**
 * Where this pack's posts stand, in one line: what went live, what Postiz
 * holds, what is approved but not sent, what is still a draft. Before this, the
 * answer was eight cards to scroll through.
 */
function PackStatusSummary({ variants }: { variants: SocialVariantView[] }) {
  const live = variants.filter((v) => v.status !== "skipped");
  if (live.length === 0) return null;
  const count = (pred: (v: SocialVariantView) => boolean) => live.filter(pred).length;
  const parts = [
    { n: count((v) => v.status === "posted"), label: "פורסמו", cls: "bg-emerald-600 text-white" },
    { n: count((v) => v.status === "scheduled"), label: "מתוזמנים בפוסטיז", cls: "bg-sky-600 text-white" },
    { n: count((v) => v.status === "approved"), label: "מאושרים ולא נשלחו", cls: "bg-amber-200 text-amber-950" },
    { n: count((v) => v.status === "draft"), label: "טיוטות", cls: "bg-neutral-200 text-neutral-700" },
    { n: count((v) => v.status === "awaiting_media"), label: "ממתינים לווידאו", cls: "bg-violet-100 text-violet-900" },
    { n: count((v) => v.status === "failed"), label: "נכשלו", cls: "bg-rose-600 text-white" },
  ].filter((p) => p.n > 0);
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      <span className="text-muted">{live.length} פוסטים:</span>
      {parts.map((p) => (
        <span key={p.label} className={`rounded px-1.5 py-0.5 font-medium ${p.cls}`}>{p.n} {p.label}</span>
      ))}
    </div>
  );
}

const STATUS_HE: Record<string, { label: string; cls: string; title: string }> = {
  draft: { label: "טיוטה", cls: "bg-canvas text-muted", title: "נוצר ולא אושר. לא יישלח לשום מקום." },
  awaiting_media: {
    label: "ממתין לווידאו",
    cls: "bg-sky-50 text-sky-900",
    title: "הכיתוב מוכן, אבל אין עדיין וידאו לפרסם עליו. לחצו \"יצירת סרטונים\" למעלה. לא ניתן לאשר או לשלוח עד אז, וזה בכוונה: שליחה בלי מדיה מסמנת את הווריאנט ככישלון קבוע.",
  },
  approved: { label: "מאושר", cls: "bg-emerald-50 text-emerald-800", title: "אושר כאן. עדיין לא נשלח ל-Postiz." },
  scheduled: { label: "בפוסטיז", cls: "bg-amber-50 text-amber-900", title: "נמסר לפוסטיז — כטיוטה שממתינה לאישור שם, או מאושר ומתוזמן לפרסום אם נשלח כך מהיומן." },
  posted: { label: "פורסם", cls: "bg-emerald-50 text-emerald-800", title: "פורסם בפועל." },
  failed: { label: "נכשל", cls: "bg-rose-50 text-rose-900", title: "השליחה נכשלה. הסיבה מופיעה מתחת." },
  skipped: { label: "דולג", cls: "bg-canvas text-muted", title: "לא נשלח במכוון." },
};

/**
 * Per-element size multipliers. Multipliers rather than pixel values so one
 * setting survives a switch between portrait, square and story: a px size tuned
 * on a portrait slide is simply wrong on a story.
 */
/** The two brand assets that exist, plus none. A closed set on purpose: an
 *  arbitrary upload here is how an off-brand mark reaches a published post. */
const LOGO_CHOICES: { id: string; label: string; hint: string }[] = [
  { id: "lockup", label: "לוגו מלא", hint: "הלוקאפ עם שורת התיוג בעברית." },
  { id: "mark", label: "סמל", hint: "הסמל בלבד, בלי שורת התיוג — לפריים שכבר אומר את המשפט." },
  { id: "none", label: "בלי", hint: "בלי לוגו על השקופית." },
];

/**
 * Where the bottom stack starts, per format — mirrors `LIFT_BASE` in the
 * renderer. Used to say where the logo lands, not to lay anything out.
 */
const LIFT_BASE: Record<string, number> = { portrait: 62, square: 62, story: 96 };
/** What the platform's own chrome covers at the bottom of a story. */
const STORY_CHROME_PX = 300;

const SCALE_CONTROLS: { key: string; label: string }[] = [
  { key: "kicker", label: "כותרת עליונה" },
  { key: "title", label: "כותרת" },
  { key: "body", label: "טקסט" },
  { key: "stat", label: "מספרים" },
  { key: "list", label: "רשימה" },
  { key: "badge", label: "כפתור" },
  { key: "logo", label: "לוגו" },
];

function Pill({ status }: { status: string }) {
  const s = STATUS_HE[status] ?? STATUS_HE.draft!;
  return (
    <span title={s.title} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Issues({ issues }: { issues: SocialCheckIssue[] }) {
  if (issues.length === 0) return null;
  const errors = issues.filter((i) => i.severity === "error");
  const warns = issues.filter((i) => i.severity === "warn");
  return (
    <div className="space-y-1">
      {errors.map((i, k) => (
        <p key={`e${k}`} className="flex items-start gap-1.5 text-xs text-danger">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            <span className="font-mono opacity-70">[{i.rule}]</span> {i.message}
          </span>
        </p>
      ))}
      {warns.map((i, k) => (
        <p key={`w${k}`} className="flex items-start gap-1.5 text-xs text-amber-700">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>
            <span className="font-mono opacity-70">[{i.rule}]</span> {i.message}
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * Instagram Reels go out as Trial Reels — shown to non-followers first — unless
 * a post says otherwise. Read from the row so an old post keeps its answer, and
 * the default is ON (`isTrialReel` on the server is the same rule).
 */
function trialReelOf(v: SocialVariantView): boolean {
  if (v.platform !== "instagram" || v.format !== "reel") return false;
  return v.postizSettings?.["is_trial_reel"] !== false;
}

function VariantCard({ variant, onChanged }: { variant: SocialVariantView; onChanged: () => void }) {
  const [body, setBody] = useState(variant.body);
  const [title, setTitle] = useState(variant.title ?? "");
  const [tags, setTags] = useState(variant.hashtags.join(" "));
  const [scale, setScale] = useState<Record<string, number | string>>(variant.scale ?? {});
  const [trialReel, setTrialReel] = useState(trialReelOf(variant));
  const [nonce, setNonce] = useState(0);

  // Re-seed when the server hands back a different row (e.g. after a send).
  useEffect(() => {
    setBody(variant.body);
    setTitle(variant.title ?? "");
    setTags(variant.hashtags.join(" "));
    setScale(variant.scale ?? {});
    setTrialReel(trialReelOf(variant));
  }, [variant.id, variant.updatedAt]);

  const dirty =
    body !== variant.body ||
    title !== (variant.title ?? "") ||
    tags !== variant.hashtags.join(" ") ||
    trialReel !== trialReelOf(variant) ||
    JSON.stringify(scale) !== JSON.stringify(variant.scale ?? {});

  // A copy edit on a variant already in Postiz is pushed through, which is a
  // withdraw and a re-create over there. Whether that worked is not something to
  // leave the operator to discover in the Postiz calendar.
  const [postizNote, setPostizNote] = useState<{ ok: boolean; text: string } | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.updateSocialVariant(variant.id, {
        body,
        title: title.trim() === "" ? null : title,
        hashtags: tags.split(/[\s,]+/).map((t) => t.replace(/^#/, "")).filter(Boolean),
        scale: Object.keys(scale).length === 0 ? null : scale,
        ...(trialReel !== trialReelOf(variant) ? { trialReel } : {}),
      }),
    onSuccess: (res) => {
      setPostizNote(
        !res.postiz
          ? null
          : res.postiz.ok
            ? { ok: true, text: res.postiz.withdrew ? "עודכן גם בפוסטיז — הפוסט הקודם הוסר ונוצר חדש" : "נשלח לפוסטיז" }
            : { ok: false, text: res.postiz.error },
      );
      onChanged();
    },
  });

  const approve = useMutation({
    mutationFn: () => api.updateSocialVariant(variant.id, { status: "approved" }),
    onSuccess: onChanged,
  });

  const send = useMutation({
    mutationFn: () => api.sendSocialVariant(variant.id),
    onSuccess: onChanged,
  });

  // Every variant that RENDERS slides gets the editor, not only the carousel:
  // a story is one too, and it was being tuned against the wrong aspect.
  const hasSlides = variant.slides.length > 0;
  const slideFormat =
    variant.format === "story" || variant.format === "photo_post" ? "story" : "portrait";
  const sent = variant.status === "scheduled" || variant.status === "posted";

  return (
    <section className="rounded-xl border border-line bg-surface p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold">
          {PLATFORM_HE[variant.platform]} · {FORMAT_HE[variant.format]}
        </h4>
        <Pill status={variant.status} />
        {!variant.checksOk && (
          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-900">
            נכשל בבדיקות
          </span>
        )}
        {/* WHEN, and where to see it. A published post says when it went live
            and links to it; anything not out yet says the moment it is
            scheduled for. Date and time, in Israel time — a date alone could
            not tell a morning post from an evening one. */}
        {variant.status === "posted" ? (
          <span className="flex items-center gap-1.5 text-[11px] text-emerald-800">
            {variant.postedAt && <>פורסם {fmtIl(variant.postedAt)}</>}
            {variant.postizReleaseUrl && (
              <a
                href={variant.postizReleaseUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-0.5 font-medium underline"
                title="הפוסט החי ברשת, כפי שפוסטיז דיווח"
              >
                לפוסט החי <ExternalLink size={11} />
              </a>
            )}
          </span>
        ) : variant.scheduledFor ? (
          <span className="text-[11px] text-muted" title={variant.status === "scheduled" ? "מתוזמן בפוסטיז לזמן הזה." : "המשבצת ביומן. עוד לא בפוסטיז."}>
            {variant.status === "scheduled" ? "מתוזמן ל־" : ""}{fmtIl(variant.scheduledFor)}
          </span>
        ) : null}
        <span className="grow" />
        {variant.linkCode ? (
          <span className="flex items-center gap-1 text-[11px] text-muted" title="הלינק בפוסט נקי. ה-UTM נוסף בצד השרת בזמן ההפניה, ואף אחד לא רואה אותו.">
            <Link2 size={12} /> /a/{variant.linkCode}
          </span>
        ) : (
          <span className="text-[11px] text-muted" title="בפלטפורמה הזו לינק בכיתוב אינו לחיץ, ולכן ה-CTA מפנה לקישור בביו.">
            קישור בביו
          </span>
        )}
        {variant.clickCount > 0 && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-700">
            <MousePointerClick size={12} /> {variant.clickCount}
          </span>
        )}
      </header>

      {variant.error && (
        <p className="mb-3 rounded-lg bg-danger/10 p-2 text-xs text-danger">{variant.error}</p>
      )}

      <div className="mb-3">
        <Issues issues={variant.checks} />
      </div>

      {variant.platform === "youtube" && (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-muted">כותרת (2-100 תווים)</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm"
            dir="rtl"
          />
        </label>
      )}

      {variant.format === "story" ? (
        <p className="rounded-lg bg-canvas px-3 py-2 text-xs text-muted">
          סטורי יוצא בלי כיתוב ובלי האשטגים, רק הפריימים.
        </p>
      ) : (<>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-muted">
          כיתוב · השורה הראשונה היא הוו, ולרוב היא כל מה שנראה לפני "עוד"
        </span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          dir="rtl"
          className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm leading-relaxed"
        />
      </label>

      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-muted">האשטגים · מופרדים ברווח, בלי #</span>
        <input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          dir="rtl"
          className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm"
        />
      </label>
      </>)}

      {/* Reels only, and Instagram only: nothing else can be a trial. */}
      {variant.platform === "instagram" && variant.format === "reel" && (
        <label className="mt-3 flex items-start gap-2 rounded-lg border border-line px-3 py-2">
          <input type="checkbox" checked={trialReel} onChange={(e) => setTrialReel(e.target.checked)} className="mt-0.5" />
          <span className="text-xs leading-relaxed">
            <span className="font-medium text-ink">ריל ניסיון (Trial Reel)</span>
            <br />
            <span className="text-muted">
              מוצג קודם רק למי שלא עוקב אחרינו, ואינסטגרם משחררת אותו לעוקבים בעצמה אם הוא מצליח. סרטון אחד בדיוק.
            </span>
          </span>
        </label>
      )}

      {hasSlides && (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-muted">
              {variant.slides.length} שקופיות ·{" "}
              {slideFormat === "story"
                ? "סטורי — הפריימים מתקדמים בנגיעה"
                : "סדר ההחלקה מימין לשמאל"}
              {" · "}
              {slideFormat === "story" ? "1080×1920" : "1080×1350"}
            </span>
            <button
              onClick={() => setNonce((n) => n + 1)}
              className="text-[11px] text-muted underline decoration-dotted hover:text-ink"
            >
              רענון תצוגה
            </button>
          </div>

          {/* WRAPS rather than scrolls. A transformed iframe inside an
              `overflow-x-auto` container never paints — every slide preview in
              this panel has been a blank rectangle since it was built, with a
              fully loaded and styled document inside it. */}
          <div className="flex flex-wrap gap-2 pb-2" dir="rtl">
            {variant.slides.map((s, i) => (
              <div key={i} className="shrink-0">
                <SlideThumb
                  variantId={variant.id}
                  index={i}
                  scale={scale}
                  width={slideFormat === "story" ? 116 : 148}
                  nonce={nonce}
                  format={slideFormat}
                />
                <p className="mt-1 text-center text-[10px] text-muted">
                  {i + 1}. {ROLE_HE[s.role] ?? s.role}
                </p>
              </div>
            ))}
          </div>

          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted">גודל האלמנטים</summary>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
              {SCALE_CONTROLS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-[11px]">
                  <span className="w-20 shrink-0 text-muted">{c.label}</span>
                  <input
                    type="range"
                    min={0.6}
                    max={1.6}
                    step={0.05}
                    value={Number(scale[c.key] ?? 1)}
                    onChange={(e) => setScale((s) => ({ ...s, [c.key]: Number(e.target.value) }))}
                    className="grow"
                  />
                  <span className="w-9 shrink-0 text-left font-mono text-muted">
                    {Number(scale[c.key] ?? 1).toFixed(2)}
                  </span>
                </label>
              ))}
            </div>

            {/* PIXELS, and kept apart from the multipliers above for that
                reason. This overrides the brand default set in ברנד → שקופיות
                מאמרים; use it when one deck sits wrong, not to re-decide the
                format. */}
            <label className="mt-3 flex items-center gap-2 text-[11px]">
              <span className="w-20 shrink-0 text-muted">מרחק מלמטה</span>
              <input
                type="range"
                min={0}
                max={400}
                step={5}
                value={Number(scale.lift ?? 0)}
                onChange={(e) => setScale((s) => ({ ...s, lift: Number(e.target.value) }))}
                className="grow"
              />
              <span className="w-14 shrink-0 text-left font-mono text-muted">
                {Number(scale.lift ?? 0)}px
              </span>
            </label>
            <p className="mt-1 text-[10px] text-muted">
              הלוגו ב־
              <span className="font-mono text-ink">
                {(LIFT_BASE[slideFormat] ?? 62) + Number(scale.lift ?? 0)}px
              </span>{" "}
              מלמטה.
              {slideFormat === "story" &&
                ((LIFT_BASE.story ?? 96) + Number(scale.lift ?? 0) < STORY_CHROME_PX ? (
                  <span className="text-danger">
                    {" "}שורת התגובה של אינסטגרם מכסה את {STORY_CHROME_PX}px התחתונים.
                  </span>
                ) : (
                  <span className="text-emerald-700"> מעל שורת התגובה.</span>
                ))}
            </p>

            {/* Which mark, as opposed to how big. On a story the full lockup
                carries the Hebrew tagline, and a closing frame that already
                states the positioning line ends up with two taglines stacked. */}
            <div className="mt-3 flex items-center gap-2 text-[11px]">
              <span className="w-20 shrink-0 text-muted">לוגו</span>
              {LOGO_CHOICES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setScale((s) => ({ ...s, logoAsset: c.id }))}
                  title={c.hint}
                  className={`rounded border px-2 py-1 ${
                    (scale.logoAsset ?? "lockup") === c.id
                      ? "border-brand-700 bg-brand-50 text-brand-800"
                      : "border-line hover:bg-canvas"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* What the save did to the post in Postiz. A refusal here is the one that
          matters — it means the post is already public and the words on screen
          no longer describe what went out. */}
      {postizNote && (
        <p
          dir="auto"
          className={`mt-3 rounded-lg px-3 py-2 text-[11px] ${
            postizNote.ok ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"
          }`}
        >
          {postizNote.text}
        </p>
      )}

      <footer className="mt-4 flex flex-wrap items-center gap-2">
        <button
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium disabled:opacity-40"
        >
          {save.isPending ? <Loader2 size={13} className="animate-spin" /> : "שמירה"}
        </button>

        {variant.status === "draft" && (
          <button
            disabled={dirty || approve.isPending || !variant.checksOk}
            onClick={() => approve.mutate()}
            title={!variant.checksOk ? "יש בדיקות שנכשלו. תקנו אותן קודם." : dirty ? "יש שינויים שלא נשמרו." : ""}
            className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            <Check size={13} /> אישור
          </button>
        )}

        <button
          disabled={sent || send.isPending || variant.status === "draft" || variant.status === "awaiting_media"}
          onClick={() => send.mutate()}
          title={
            variant.status === "awaiting_media"
              ? 'ממתין לווידאו. לחצו "יצירת סרטונים" קודם.'
              : variant.status === "draft"
                ? "צריך לאשר קודם."
                : sent
                  ? "כבר נשלח כטיוטה ל-Postiz."
                  : "יוצר טיוטה ב-Postiz. לא מפרסם."
          }
          className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          {send.isPending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          שליחת טיוטה ל-Postiz
        </button>

        {send.isError && <span className="text-xs text-danger">{(send.error as Error).message}</span>}
      </footer>
    </section>
  );
}

function PackRefreshButton({ packId, onDone }: { packId: string; onDone: () => void }) {
  const [job, setJob] = useState<PackRefreshJobView | null>(null);
  const [armed, setArmed] = useState(false);
  const running = job?.status === "running";

  // Re-attach to a refresh already running for this pack.
  useEffect(() => {
    void api.packRefreshStatus(packId).then((r) => { if (r.job?.status === "running") setJob(r.job); }).catch(() => {});
  }, [packId]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      void api.packRefreshStatus(packId).then((r) => {
        if (!r.job) return;
        setJob(r.job);
        if (r.job.status !== "running") onDone();
      }).catch(() => {});
    }, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, packId]);

  const start = useMutation({
    mutationFn: () => api.refreshSocialPack(packId, "rewrite"),
    onSuccess: (r) => { setArmed(false); setJob(r.job); },
    onError: () => {
      setArmed(false);
      void api.packRefreshStatus(packId).then((r) => { if (r.job) setJob(r.job); }).catch(() => {});
    },
  });

  const done = job && job.status !== "running" ? job : null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <button
        onClick={() => (armed ? start.mutate() : setArmed(true))}
        disabled={running || start.isPending}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 disabled:opacity-50 ${armed ? "border-amber-600 bg-amber-50 text-amber-900" : "border-line"}`}
        title="כותב מחדש את הפוסטים שעוד לא נשלחו, לפי הפרומפט וכללי העברית של היום. עולה כסף (קריאה אחת לחבילה)."
      >
        {running || start.isPending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
        {running ? "כותב מחדש… (אפשר לעזוב את הדף)" : armed ? "לאשר כתיבה מחדש" : "כתיבה מחדש לפי כללי העברית"}
      </button>
      {armed && !running && (
        <span className="text-amber-900">
          פוסטים שכבר בפוסטיז או פורסמו לא ישתנו. תאריכים ולינקים נשמרים. פוסט מאושר יחזור לטיוטה.{" "}
          <button onClick={() => setArmed(false)} className="underline">ביטול</button>
        </span>
      )}
      {done && (
        <span className={done.status === "done" ? "text-emerald-700" : "text-danger"}>
          {done.status === "done"
            ? `נכתבו מחדש ${done.outcome?.changed.length ?? 0} פוסטים · $${(done.outcome?.costUsd ?? 0).toFixed(3)}${done.outcome?.rejected.length ? ` · ${done.outcome.rejected.length} נדחו` : ""}`
            : `נכשל: ${done.error}`}
        </span>
      )}
    </div>
  );
}

export function SocialPackTab({ keywordId }: { keywordId: string }) {
  const qc = useQueryClient();
  const packsQuery = useQuery({
    queryKey: ["social-packs", keywordId],
    queryFn: () => api.listSocialPacks({ keywordId }),
  });

  const packs = packsQuery.data?.packs ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const packId = selectedId ?? packs[0]?.id ?? null;

  const packQuery = useQuery({
    queryKey: ["social-pack", packId],
    queryFn: () => api.getSocialPack(packId!),
    enabled: Boolean(packId),
  });

  const generate = useMutation({
    // The server derives the published URL from the article slug when none is
    // given, the same way the existing one-click Postiz send does.
    mutationFn: () => api.generateSocialPack(keywordId),
    onSuccess: (res) => {
      setSelectedId(res.packId);
      void qc.invalidateQueries({ queryKey: ["social-packs", keywordId] });
    },
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["social-pack", packId] });
    void qc.invalidateQueries({ queryKey: ["social-packs", keywordId] });
  };

  // A render is a background job, so the button starts it and this polls. The
  // poll STOPS once the job is not rendering — a status endpoint hit every two
  // seconds forever is a slow leak nobody notices.
  const videoJob = useQuery({
    queryKey: ["social-video-status", keywordId],
    queryFn: () => api.socialVideoStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "rendering" ? 2000 : false),
  });
  const rendering = videoJob.data?.status === "rendering";

  // The explainer SPENDS (footage per shot plus one script call); the deck is
  // free. The default is the explainer because that is the format the operator
  // approved, and the cost is stated on the button rather than discovered later.
  const [videoStyle, setVideoStyle] = useState<"explainer" | "deck">("explainer");
  const [reelSeconds, setReelSeconds] = useState(30);

  // The explainer films an APPROVED storyboard and nothing else; the deck spends
  // nothing and needs no plan, so it still starts from a plain press.
  const makeVideos = useMutation<unknown, Error, number | undefined>({
    mutationFn: (storyboardId) =>
      api.startSocialVideos(keywordId, {
        ...(packId ? { packId } : {}),
        // Filming a storyboard is always the explainer, whatever the selector
        // above happens to be showing. A plan approved as a shot list and then
        // rendered as an animated carousel is not the thing that was approved.
        style: storyboardId !== undefined ? "explainer" : videoStyle,
        targetSeconds: reelSeconds,
        ...(storyboardId !== undefined ? { storyboardId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social-video-status", keywordId] });
      void qc.invalidateQueries({ queryKey: ["storyboards", keywordId] });
    },
  });

  // Variants whose copy is finished and whose video does not exist yet. This is
  // what the button is FOR, so it says how many rather than being always-on.
  const awaiting = (packQuery.data?.variants ?? []).filter((v) => v.status === "awaiting_media");

  // When a render finishes it moves variants out of awaiting_media, so the pack
  // on screen is stale until it is refetched.
  useEffect(() => {
    if (videoJob.data?.status === "done") invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoJob.data?.status, videoJob.data?.finishedAt]);

  const dropped = packQuery.data?.pack.dropped ?? [];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {generate.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {packs.length === 0 ? "יצירת חבילה סוציאלית" : "יצירת חבילה נוספת"}
        </button>

        {packs.length > 1 && (
          <select
            value={packId ?? ""}
            onChange={(e) => setSelectedId(e.target.value)}
            className="rounded-lg border border-line bg-canvas px-2 py-1.5 text-xs"
          >
            {packs.map((p: SocialPackView) => (
              <option key={p.id} value={p.id}>
                {new Date(p.createdAt).toLocaleString("he-IL")} · {p.status}
              </option>
            ))}
          </select>
        )}

        {/* Re-write this pack's unsent posts under today's prompt and Hebrew
            rules. Packs written before those rules still said things like
            "חכים לכן"; this is how an existing pack catches up without a new
            pack, new links and new slots. */}
        {packId && <PackRefreshButton packId={packId} onDone={() => void packQuery.refetch()} />}

        {packId && awaiting.length > 0 && (
          <div className="flex items-center gap-1.5">
            <select
              value={videoStyle}
              onChange={(e) => setVideoStyle(e.target.value as "explainer" | "deck")}
              disabled={rendering || makeVideos.isPending}
              className="rounded-lg border border-line bg-canvas px-2 py-2 text-xs"
              title="explainer = צילום מיוצר עם קריינות (עולה כסף). שקופיות = הקרוסלה המונפשת (חינם, אבל לא מחזיקה קשב)."
            >
              <option value="explainer">סרטון explainer</option>
              <option value="deck">שקופיות מונפשות</option>
            </select>

            {videoStyle === "explainer" && (
              <select
                value={reelSeconds}
                onChange={(e) => setReelSeconds(Number(e.target.value))}
                disabled={rendering || makeVideos.isPending}
                className="rounded-lg border border-line bg-canvas px-2 py-2 text-xs"
                title="אורך הריל המוגמר, כולל כרטיס הסיום. ריל ארוך יותר הוא עוד שוטים, לא שוטים ארוכים יותר."
              >
                {[20, 30, 45, 60].map((n) => (
                  <option key={n} value={n}>{n} שניות</option>
                ))}
              </select>
            )}

            {/* No explainer button here on purpose. It buys footage, and footage
                now starts from an approved plan in the storyboard panel below —
                a single press that went from an article to two dollars of clips
                is exactly what this replaced. */}
            {videoStyle === "explainer" ? (
              <span className="text-xs text-muted">
                {awaiting.length} ווריאנטים ממתינים — מתחילים מתוכנית בסטוריבורד למטה
              </span>
            ) : (
              <button
                onClick={() => makeVideos.mutate(undefined)}
                disabled={rendering || makeVideos.isPending}
                title={
                  rendering
                    ? "רינדור רץ ברקע. אפשר לעזוב את המסך."
                    : `${awaiting.length} ווריאנטים ממתינים לווידאו. מנפיש את הקרוסלה — בלי עלות API, אבל זה הפורמט שנשפט כלא מחזיק קשב.`
                }
                className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-3 py-2 text-sm font-medium text-brand-700 disabled:opacity-50"
              >
                {rendering || makeVideos.isPending ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
                יצירת סרטונים ({awaiting.length})
              </button>
            )}
          </div>
        )}

        {packQuery.data && (
          <div className="w-full">
            <PackStatusSummary variants={packQuery.data.variants} />
          </div>
        )}

        {generate.isError && <span className="text-xs text-danger">{(generate.error as Error).message}</span>}
        {makeVideos.isError && <span className="text-xs text-danger">{(makeVideos.error as Error).message}</span>}
      </header>

      {/* Where a reel actually starts: plan → read → approve → film → watch →
          release. Both gates live here. */}
      {packId && (
        <StoryboardPanel
          keywordId={keywordId}
          onFilm={(storyboardId) => makeVideos.mutate(storyboardId)}
          filming={rendering || makeVideos.isPending}
        />
      )}

      {videoJob.data && videoJob.data.status !== "idle" && (
        <div className="rounded-lg border border-line bg-canvas p-3 text-xs">
          {rendering && (
            <p className="flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" />
              מרנדר סרטון{videoJob.data.stage ? ` · ${videoJob.data.stage}` : ""}
              {videoJob.data.stageDetail ? ` (${videoJob.data.stageDetail})` : ""} — אפשר לעזוב את המסך.
            </p>
          )}
          {videoJob.data.status === "done" && videoJob.data.summary && (
            <p>
              נוצר סרטון של {videoJob.data.summary.durationSec} שניות
              {typeof videoJob.data.summary.slides === "number" ? ` מ-${videoJob.data.summary.slides} חלקים` : ""}
              {typeof videoJob.data.summary.variantsFreed === "number"
                ? ` · ${videoJob.data.summary.variantsFreed} ווריאנטים שוחררו לעריכה`
                : ""}
            </p>
          )}
          {videoJob.data.status === "error" && (
            <p className="text-danger">רינדור נכשל: {videoJob.data.error}</p>
          )}
          {(videoJob.data.warnings ?? []).map((w, i) => (
            <p key={i} className="mt-1 text-muted">{w}</p>
          ))}
        </div>
      )}

      {generate.data && (
        <div className="rounded-lg border border-line bg-canvas p-3 text-xs">
          <p>
            נוצרו {generate.data.deck} שקופיות ו-{generate.data.accepted.length} ווריאנטים · עלות $
            {generate.data.costUsd.toFixed(4)} · {generate.data.attempts} ניסיון/ות
          </p>
          {generate.data.rejected.length > 0 && (
            <div className="mt-2 space-y-1">
              {generate.data.rejected.map((r) => (
                <p key={r.platform} className="text-danger">
                  {PLATFORM_HE[r.platform]} נדחה: {r.issues.map((i) => i.message).join(" · ")}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {packQuery.isPending && packId && (
        <p className="flex items-center gap-2 text-sm text-muted">
          <Loader2 size={14} className="animate-spin" /> טוען…
        </p>
      )}

      {!packId && !generate.isPending && (
        <p className="text-sm text-muted">
          אין עדיין חבילה למאמר הזה. היצירה מפיקה קרוסלה וכיתוב לכל פלטפורמה מחוברת, ועוצרת בטיוטה.
        </p>
      )}

      {dropped.length > 0 && (
        <details className="rounded-lg border border-line bg-canvas p-3">
          <summary className="cursor-pointer text-xs font-medium text-muted">
            {dropped.length} דברים שנשמטו, עם הסיבה
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] text-muted">
            {dropped.map((d, i) => (
              <li key={i}>
                <span className="font-mono">{d.role}</span> — {d.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {packQuery.data?.variants.map((v: SocialVariantView) => (
        <VariantCard key={v.id} variant={v} onChanged={invalidate} />
      ))}
    </div>
  );
}
